// Source: https://www.six-group.com/dam/download/banking-services/standardization/qr-bill/ig-qr-bill-v2.2-de.pdf

import {
	BankAccountEntityType,
	ClientEntityType,
	GuarantorEntityType,
} from "@/lib/supabase/supabaseTypes";
import { AdrTp, Ccy } from "./qr-payment-types";
import {
	isAddInfValid,
	isAmtValid,
	checkQrIban,
	isRefValid,
	isStrdBkgInfValid,
	isUstrdValid,
} from "./qr-payment-validation-functions";
import { generateQrReferenceNumber } from "@/dentlab/src/lib/utils/qr-reference-generator";
import { Currency } from "@/dentlab/src/types/enums";
import { AddressRecipient } from "../../utils";
import {
	getPaymentSlipDebtorAddress,
	validatePaymentSlipCreditor,
	validatePaymentSlipDebtor,
} from "./payment-slip-parties";

export interface PaymentSlipDataInput {
	bankAccount: BankAccountEntityType;
	recipient: AddressRecipient;
	amount: string;
	currency: string;
	unstructuredMessage?: string;
	rechnungsInfo?: string;
	av1Param?: string;
	av2Param?: string;
	invoiceNumber: string;
}

/**
 * Zahlungsempfänger Informationen (Obligatorisch):
 * @param {string} KONTO - (IBAN) IBAN bzw. QR-IBAN des Begünstigten
 * ---
 * Zahlungsempfänger:
 * @param {AdrTp} ZE_ADRESS_TYP - (AdrTp) Adresstyp des Zahlungsempfängers
 * @param {string} ZE_NAME - (Name) Name bzw. Firma des Zahlungsempfängers gemäss Kontobezeichnung
 * @param {string} ZE_STRASSE_ODER_ADRESSZEILE_1 (StrtNmOrAdrLine1) - Strasse oder Adresszeile 1 des Zahlungsempfängers
 * @param {string} ZE_HAUSNUMMER_ODER_ADRESSZEILE_2 (BldgNbOrAdrLine2) - Hausnummer oder Adresszeile 2 des Zahlungsempfängers
 * @param {string} ZE_POSTLEITZAHL (PstCd) - Postleitzahl des Zahlungsempfängers
 * @param {string} ZE_ORT (TwnNm) - Ort des Zahlungsempfängers
 * @param {string} ZE_LAND (Ctry) - Land des Zahlungsempfängers
 * ---
 * Zahlungsempfänger Ersatzadresse: Informationen zum endgültigen Zahlungsempfänger, darf aktuell noch nicht befüllt werden.
 * ...
 * ---
 * Zahlbetragsinformationen (Obligatorisch):
 * @param {string} BETRAG (Amt) - Zahlbetrag
 * @param {string} WAEHRUNG (Ccy) - Währung
 * ---
 * Endgültiger Zahlungspflichtiger (Optional):
 * @param {boolean} IS_EZP - (boolean) true, wenn ein endgültiger Zahlungspflichtiger angegeben werden soll
 * @param {AdrTp} EZP_ADRESS_TYP (AdrTp) - Adresstyp des endgültigen Zahlungspflichtigen
 * @param {string} EZP_NAME (Name) - Name bzw. Firma des endgültigen Zahlungspflichtigen
 * @param {string} EZP_STRASSE_ODER_ADRESSZEILE_1 (StrtNmOrAdrLine1) - Strasse oder Adresszeile 1 des endgültigen Zahlungspflichtigen
 * @param {string} EZP_HAUSNUMMER_ODER_ADRESSZEILE_2 (BldgNbOrAdrLine2) - Hausnummer oder Adresszeile 2 des endgültigen Zahlungspflichtigen
 * @param {string} EZP_POSTLEITZAHL (PstCd) - Postleitzahl des endgültigen Zahlungspflichtigen
 * @param {string} EZP_ORT (TwnNm) - Ort des endgültigen Zahlungspflichtigen
 * @param {string} EZP_LAND (Ctry) - Land des endgültigen Zahlungspflichtigen
 * ---
 * Zahlungsreferenz (Obigatorisch):
 * Referenztyp (QRR - QR-Referenz)
 * @param {string} REFERENZ (Ref) - Referenz
 * ---
 * Zusätzliche Informationen (AddInf) (Optional, except Trailer):
 * @param {string} UNSTRUKTURIERTE_MITTEILUNG (Ustrd) - Unstrukturierte Mitteilung
 * Trailer (EPD - End Payment Data)
 * @param {string} RECHNUNGSINFORMATIONEN (StrdBkgInf) - Rechnungsinformationen - nicht Teil der Standardisierung
 * ---
 * Alternative Verfahren (Additional):
 * @param {string} AV1_PARAMETER (AltPmt) - Alternatives Verfahren Parameter
 * @param {string} AV2_PARAMETER (AltPmt) -Alternatives Verfahren Parameter
 */
export interface PaymentSlipDataOutput {
	KONTO: string;
	ZE_ADRESS_TYP: AdrTp;
	ZE_NAME: string;
	ZE_STRASSE_ODER_ADRESSZEILE_1: string;
	ZE_HAUSNUMMER_ODER_ADRESSZEILE_2: string;
	ZE_POSTLEITZAHL: string;
	ZE_ORT: string;
	ZE_LAND: string;
	BETRAG: string;
	WAEHRUNG: string;
	REFERENZ: string;
	EZP_ADRESS_TYP: AdrTp;
	EZP_NAME: string;
	EZP_STRASSE_ODER_ADRESSZEILE_1?: string;
	EZP_HAUSNUMMER_ODER_ADRESSZEILE_2?: string;
	EZP_POSTLEITZAHL: string;
	EZP_ORT: string;
	EZP_LAND: string;
	UNSTRUKTURIERTE_MITTEILUNG?: string;
	RECHNUNGSINFORMATIONEN?: string; // https://www.swiss-qr-invoice.org/downloads/qr-bill-s1-syntax-de.pdf
	AV1_PARAMETER?: string;
	AV2_PARAMETER?: string;
}

interface FormatPaymentSlipDataResult {
	success: boolean;
	data: PaymentSlipDataOutput | null;
	qrReceipt: string | null;
	error: string;
}

/**
 * Formats data necessary for payment slips into exact variables according to the Swiss QR Bill standard/syntax
 */
export const validatePaymentSlipData = ({
	bankAccount,
	recipient,
	amount,
	currency,
	invoiceNumber,
}: PaymentSlipDataInput): { success: boolean; error: string } => {
	const result = formatPaymentSlipData({
		bankAccount,
		recipient,
		amount,
		currency,
		invoiceNumber,
	});
	return { success: result.success, error: result.error };
};

const generateQrReceipt = (
	bankAccount: BankAccountEntityType,
	recipient: AddressRecipient,
	amount: string,
	currency: string,
	invoiceNumber: string,
	unstructuredMessage?: string,
	rechnungsInfo?: string,
	av1Param?: string,
	av2Param?: string
): string | null => {
	try {
		const KONTO = bankAccount.qr_iban || "";
		const ZE_ADRESS_TYP = "S";
		const ZE_NAME = bankAccount.cdtr_name || "";
		const ZE_STRASSE_ODER_ADRESSZEILE_1 =
			bankAccount.cdtr_street_line_1 || "";
		const ZE_HAUSNUMMER_ODER_ADRESSZEILE_2 =
			bankAccount.cdtr_house_number_line_2 || "";
		const ZE_POSTLEITZAHL = bankAccount.cdtr_area_code || "";
		const ZE_ORT = bankAccount.cdtr_city || "";
		const ZE_LAND = bankAccount.cdtr_country?.toUpperCase() || "CH";
		const BETRAG = amount;
		const WAEHRUNG = currency || Currency.CHF;
		const REFERENZ =
			bankAccount.reference_number && invoiceNumber
				? generateQrReferenceNumber(
						bankAccount.reference_number,
						invoiceNumber
					)
				: "";

		const EZP_ADRESS_TYP = "S";

		const debtor = getPaymentSlipDebtorAddress(recipient);
		const EZP_NAME = debtor.name;
		const EZP_STRASSE_ODER_ADRESSZEILE_1 = debtor.street;
		const EZP_HAUSNUMMER_ODER_ADRESSZEILE_2 = "";
		const EZP_POSTLEITZAHL = debtor.postalCode;
		const EZP_ORT = debtor.city;
		const EZP_LAND = debtor.country;

		const UNSTRUKTURIERTE_MITTEILUNG = unstructuredMessage || "";
		const RECHNUNGSINFORMATIONEN = rechnungsInfo || "";
		const AV1_PARAMETER = av1Param || "";
		const AV2_PARAMETER = av2Param || "";

		const result: PaymentSlipDataOutput = {
			KONTO,
			ZE_ADRESS_TYP,
			ZE_NAME,
			ZE_STRASSE_ODER_ADRESSZEILE_1,
			ZE_HAUSNUMMER_ODER_ADRESSZEILE_2,
			ZE_POSTLEITZAHL,
			ZE_ORT,
			ZE_LAND,
			BETRAG,
			WAEHRUNG,
			REFERENZ,
			EZP_ADRESS_TYP,
			EZP_NAME,
			EZP_STRASSE_ODER_ADRESSZEILE_1,
			EZP_HAUSNUMMER_ODER_ADRESSZEILE_2,
			EZP_POSTLEITZAHL,
			EZP_ORT,
			EZP_LAND,
			UNSTRUKTURIERTE_MITTEILUNG,
			RECHNUNGSINFORMATIONEN,
			AV1_PARAMETER,
			AV2_PARAMETER,
		};

		return getQrCodeReceipt(result);
	} catch (error) {
		return null;
	}
};

export const formatPaymentSlipData = ({
	bankAccount,
	recipient,
	amount,
	currency,
	unstructuredMessage,
	rechnungsInfo,
	av1Param,
	av2Param,
	invoiceNumber,
}: PaymentSlipDataInput): FormatPaymentSlipDataResult => {
	const qrReceipt = generateQrReceipt(
		bankAccount,
		recipient,
		amount,
		currency,
		invoiceNumber,
		unstructuredMessage,
		rechnungsInfo,
		av1Param,
		av2Param
	);
	if (!invoiceNumber) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: "Die Rechnungsnummer fehlt.",
		};
	}
	if (invoiceNumber.length !== 6) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: "Die Rechnungsnummer muss 6-stellig sein.",
		};
	}
	const [creditorProblem] = validatePaymentSlipCreditor(bankAccount);
	if (creditorProblem) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: creditorProblem,
		};
	}
	const KONTO = bankAccount.qr_iban ?? "";
	// Decided to always use S for now, instead of bankAccount.cdtr_adrtp
	const ZE_ADRESS_TYP = "S";
	const ZE_NAME = bankAccount.cdtr_name ?? "";
	const ZE_STRASSE_ODER_ADRESSZEILE_1 = bankAccount.cdtr_street_line_1 ?? "";
	const ZE_HAUSNUMMER_ODER_ADRESSZEILE_2 =
		bankAccount.cdtr_house_number_line_2 ?? "";
	const ZE_POSTLEITZAHL = bankAccount.cdtr_area_code ?? "";
	const ZE_ORT = bankAccount.cdtr_city ?? "";
	const ZE_LAND = bankAccount.cdtr_country?.toUpperCase() || "CH";
	const BETRAG = amount;
	if (!isAmtValid(BETRAG)) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: `BETRAG ist unzulässig (${BETRAG})`,
		};
	}
	const WAEHRUNG = currency || Currency.CHF;
	const REFERENZ = generateQrReferenceNumber(
		bankAccount.reference_number ?? "",
		invoiceNumber
	);

	// For now, we simplify by using "S" instead of allowing "S" or "K"
	const EZP_ADRESS_TYP = "S";
	const [debtorProblem] = validatePaymentSlipDebtor(recipient);
	if (debtorProblem) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: debtorProblem.message,
		};
	}
	const debtor = getPaymentSlipDebtorAddress(recipient);
	const EZP_NAME = debtor.name;
	const EZP_STRASSE_ODER_ADRESSZEILE_1 = debtor.street || undefined;
	const EZP_HAUSNUMMER_ODER_ADRESSZEILE_2 = undefined;
	const EZP_POSTLEITZAHL = debtor.postalCode;
	const EZP_ORT = debtor.city;
	const EZP_LAND = debtor.country;
	if (!isRefValid(REFERENZ)) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: `REFERENZ ist unzulässig (${REFERENZ})`,
		};
	}
	const UNSTRUKTURIERTE_MITTEILUNG = unstructuredMessage || undefined;
	if (
		UNSTRUKTURIERTE_MITTEILUNG &&
		!isUstrdValid(UNSTRUKTURIERTE_MITTEILUNG)
	) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: `UNSTRUKTURIERTE_MITTEILUNG ist unzulässig (${UNSTRUKTURIERTE_MITTEILUNG})`,
		};
	}
	const RECHNUNGSINFORMATIONEN = rechnungsInfo || undefined;
	if (RECHNUNGSINFORMATIONEN && !isStrdBkgInfValid(RECHNUNGSINFORMATIONEN)) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: `RECHNUNGSINFORMATIONEN ist unzulässig (${RECHNUNGSINFORMATIONEN})`,
		};
	}
	if (
		UNSTRUKTURIERTE_MITTEILUNG &&
		RECHNUNGSINFORMATIONEN &&
		!isAddInfValid(UNSTRUKTURIERTE_MITTEILUNG, RECHNUNGSINFORMATIONEN)
	) {
		return {
			success: false,
			data: null,
			qrReceipt: qrReceipt,
			error: `UNSTRUKTURIERTE_MITTEILUNG and RECHNUNGSINFORMATIONEN are not valid (${UNSTRUKTURIERTE_MITTEILUNG}, ${RECHNUNGSINFORMATIONEN})`,
		};
	}
	const AV1_PARAMETER = av1Param || undefined;
	const AV2_PARAMETER = av2Param || undefined;

	const result: PaymentSlipDataOutput = {
		KONTO,
		ZE_ADRESS_TYP,
		ZE_NAME,
		ZE_STRASSE_ODER_ADRESSZEILE_1,
		ZE_HAUSNUMMER_ODER_ADRESSZEILE_2,
		ZE_POSTLEITZAHL,
		ZE_ORT,
		ZE_LAND,
		BETRAG,
		WAEHRUNG,
		REFERENZ,
		EZP_ADRESS_TYP,
		EZP_NAME,
		EZP_STRASSE_ODER_ADRESSZEILE_1,
		EZP_HAUSNUMMER_ODER_ADRESSZEILE_2,
		EZP_POSTLEITZAHL,
		EZP_ORT,
		EZP_LAND,
		UNSTRUKTURIERTE_MITTEILUNG,
		RECHNUNGSINFORMATIONEN,
		AV1_PARAMETER,
		AV2_PARAMETER,
	};
	return {
		success: true,
		data: result,
		qrReceipt: getQrCodeReceipt(result),
		error: "",
	};
};

/**
 * getQrData - returns the QR-Code data for the given parameters
 * ---
 * Header (Obligatorisch):
 * QRType
 * Version
 * Coding Type
 * ---
 */
const getQrCodeReceipt = ({
	KONTO,
	ZE_ADRESS_TYP,
	ZE_NAME,
	ZE_STRASSE_ODER_ADRESSZEILE_1,
	ZE_HAUSNUMMER_ODER_ADRESSZEILE_2,
	ZE_POSTLEITZAHL,
	ZE_ORT,
	ZE_LAND,
	BETRAG,
	WAEHRUNG,
	REFERENZ,
	EZP_ADRESS_TYP,
	EZP_NAME,
	EZP_STRASSE_ODER_ADRESSZEILE_1 = "",
	EZP_HAUSNUMMER_ODER_ADRESSZEILE_2 = "",
	EZP_POSTLEITZAHL,
	EZP_ORT,
	EZP_LAND,
	UNSTRUKTURIERTE_MITTEILUNG = "",
	RECHNUNGSINFORMATIONEN = "",
	AV1_PARAMETER = "",
	AV2_PARAMETER = "",
}: PaymentSlipDataOutput): string => {
	const isQrIban = checkQrIban(KONTO);

	const QRTYPE = "SPC"; // Swiss Payment Code
	const VERSION = "0200"; // Version 2.0
	const CODING_TYPE = "1"; // UTF-8

	// EZE (Endgültiger Zahlungsempfänger) darf aktuell noch nicht befüllt werden.
	const EZE_ADRESS_TYP = "";
	const EZE_NAME = "";
	const EZE_STRASSE_ODER_ADRESSZEILE_1 = "";
	const EZE_HAUSNUMMER_ODER_ADRESSZEILE_2 = "";
	const EZE_POSTLEITZAHL = "";
	const EZE_ORT = "";
	const EZE_LAND = "";

	// Referenztyp (QR, SCOR, NON)
	const REFERENZTYP = isQrIban ? "QRR" : "NON";
	const _REFERENZ = isQrIban ? REFERENZ.replaceAll(" ", "") : "";
	const TRAILER = "EPD";

	// It is essential that there are no tabs at the beginning of the line.
	return `${QRTYPE}
${VERSION}
${CODING_TYPE}
${KONTO}
${ZE_ADRESS_TYP}
${ZE_NAME}
${ZE_STRASSE_ODER_ADRESSZEILE_1}
${ZE_HAUSNUMMER_ODER_ADRESSZEILE_2}
${ZE_POSTLEITZAHL}
${ZE_ORT}
${ZE_LAND}
${EZE_ADRESS_TYP}
${EZE_NAME}
${EZE_STRASSE_ODER_ADRESSZEILE_1}
${EZE_HAUSNUMMER_ODER_ADRESSZEILE_2}
${EZE_POSTLEITZAHL}
${EZE_ORT}
${EZE_LAND}
${BETRAG}
${WAEHRUNG}
${EZP_ADRESS_TYP}
${EZP_NAME}
${EZP_STRASSE_ODER_ADRESSZEILE_1}
${EZP_HAUSNUMMER_ODER_ADRESSZEILE_2}
${EZP_POSTLEITZAHL}
${EZP_ORT}
${EZP_LAND}
${REFERENZTYP}
${_REFERENZ}
${UNSTRUKTURIERTE_MITTEILUNG}
${TRAILER}
${RECHNUNGSINFORMATIONEN}
${AV1_PARAMETER}
${AV2_PARAMETER}`;
};
